import { useEffect, useRef, useState } from 'react'

export default function ShowcaseLightbox({ frames, index, onClose, onIndex }) {
  const closeRef = useRef(null)
  const startX = useRef(null)
  const frame = frames[index]
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    setFailed(false)
  }, [index, frame?.src])

  useEffect(() => {
    const previouslyFocused = document.activeElement
    closeRef.current?.focus()
    const onKey = (event) => {
      if (event.key === 'Escape') onClose()
      if (event.key === 'ArrowRight') onIndex((index + 1) % frames.length)
      if (event.key === 'ArrowLeft') onIndex((index - 1 + frames.length) % frames.length)
    }
    document.addEventListener('keydown', onKey)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = previousOverflow
      if (previouslyFocused instanceof HTMLElement) previouslyFocused.focus()
    }
  }, [frames.length, index, onClose, onIndex])

  if (!frame) return null

  const shift = (delta) => onIndex((index + delta + frames.length) % frames.length)

  return (
    <div
      className="fixed inset-0 z-[80] bg-black/95 flex flex-col"
      role="dialog"
      aria-modal="true"
      aria-label={frame.alt || frame.caption || 'Image'}
      onClick={onClose}
    >
      <div className="flex items-center justify-between px-4 sm:px-8 h-16 shrink-0" onClick={(event) => event.stopPropagation()}>
        <p className="text-[0.65rem] uppercase tracking-[0.22em] text-cinema-gold font-sans">
          {frame.category || 'SMA'}
          <span className="text-cinema-muted"> {index + 1} / {frames.length}</span>
        </p>
        <button
          ref={closeRef}
          type="button"
          onClick={onClose}
          className="min-h-11 min-w-11 px-3 text-xs uppercase tracking-[0.18em] text-white font-sans"
        >
          Close
        </button>
      </div>

      <div
        className="relative flex-1 min-h-0 flex items-center justify-center px-4 sm:px-16"
        onClick={(event) => event.stopPropagation()}
        onPointerDown={(event) => { startX.current = event.clientX }}
        onPointerUp={(event) => {
          if (startX.current == null) return
          const delta = event.clientX - startX.current
          if (delta > 48) shift(-1)
          if (delta < -48) shift(1)
          startX.current = null
        }}
      >
        {!failed ? (
          <img
            key={frame.src}
            src={frame.src}
            alt={frame.alt || ''}
            className="max-h-full max-w-full object-contain"
            onError={() => setFailed(true)}
          />
        ) : (
          <p className="text-[0.7rem] uppercase tracking-[0.22em] text-cinema-muted font-sans">SMA</p>
        )}
        {frames.length > 1 && (
          <>
            <button
              type="button"
              onClick={() => shift(-1)}
              className="hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 min-h-11 min-w-11 items-center justify-center text-white/80 hover:text-white"
              aria-label="Previous image"
            >
              ←
            </button>
            <button
              type="button"
              onClick={() => shift(1)}
              className="hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 min-h-11 min-w-11 items-center justify-center text-white/80 hover:text-white"
              aria-label="Next image"
            >
              →
            </button>
          </>
        )}
      </div>

      <div className="px-4 sm:px-8 py-5 shrink-0" onClick={(event) => event.stopPropagation()}>
        {frame.caption && <p className="font-display text-2xl text-white font-light">{frame.caption}</p>}
        {frame.creator && <p className="mt-1 text-sm text-cinema-soft font-sans">Created by {frame.creator}</p>}
        {frame.alt && <p className="mt-1 text-sm text-cinema-muted font-sans">{frame.alt}</p>}
      </div>
    </div>
  )
}
